import React from "react";
import { Box, Container, Typography, Grid } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import MapIcon from "@mui/icons-material/Map";
import LayersIcon from "@mui/icons-material/Layers";
import RadarIcon from "@mui/icons-material/Radar";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LinkIcon from "@mui/icons-material/Link";
import VerifiedIcon from "@mui/icons-material/Verified";

// ── Animations ────────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Styled ────────────────────────────────────────────────────────────────────
const PageWrapper = styled(Box)({
  minHeight: "100vh",
  background: "#ffffff",
  fontFamily: "'Roboto', sans-serif",
});

const HeroBanner = styled(Box)({
  position: "relative",
  width: "100%",
  height: "220px",
  background: `
    url('/view.jpg')
  `,
  backgroundSize: "cover",
  backgroundPosition: "center 40%",
  display: "flex",
  alignItems: "flex-end",
  padding: "0 0 28px 0",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: "linear-gradient(90deg, #1565C0, #42A5F5, #1565C0)",
  },
});

const HeroTitle = styled(Typography)({
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 700,
  fontSize: "clamp(1.75rem, 3.8vw, 2.4rem)",
  color: "#ffffff",
  letterSpacing: "0.03em",
  textTransform: "uppercase",
  animation: `${fadeUp} 0.6s ease both`,
  textShadow: "0 2px 12px rgba(0,0,0,0.5)",
});

const ContentCard = styled(Box)({
  background: "#ffffff",
  border: "1px solid #E3F2FD",
  borderRadius: "4px",
  padding: "32px 36px",
  marginBottom: "20px",
  boxShadow: "0 1px 4px rgba(21,101,192,0.08)",
});

const SectionDivider = styled(Box)({
  width: "44px",
  height: "3px",
  background: "linear-gradient(90deg, #1565C0, #42A5F5)",
  borderRadius: "2px",
  marginBottom: "14px",
});

const SectionHeading = styled(Typography)({
  fontFamily: "'Roboto', sans-serif",
  fontSize: "1.3rem",
  fontWeight: 700,
  color: "#1565C0",
  marginBottom: "18px",
  letterSpacing: "0.01em",
});

const BodyParagraph = styled(Typography)({
  fontFamily: "'Roboto', sans-serif",
  fontSize: "1.02rem",
  color: "#424242",
  lineHeight: 1.85,
  marginBottom: "18px",
  "&:last-child": { marginBottom: 0 },
});

const FeatureItem = styled(Box)({
  display: "flex",
  gap: "14px",
  padding: "14px 16px",
  borderRadius: "4px",
  border: "1px solid #E3F2FD",
  background: "#FAFAFA",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: "#1565C0",
    background: "#E3F2FD",
    boxShadow: "0 2px 10px rgba(21,101,192,0.15)",
    transform: "translateY(-2px)",
  },
});

const FeatureIconWrap = styled(Box)({
  width: "38px",
  height: "38px",
  flexShrink: 0,
  borderRadius: "6px",
  background: "linear-gradient(135deg, #E3F2FD, #BBDEFB)",
  border: "1px solid #90CAF9",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#1565C0",
  marginTop: "1px",
});

const StakeholderRow = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: "14px",
  padding: "14px 0",
  borderBottom: "1px solid #F5F5F5",
  "&:last-child": { borderBottom: "none" },
});

const AccentNumber = styled(Box)({
  width: "26px",
  height: "26px",
  flexShrink: 0,
  borderRadius: "50%",
  background: "linear-gradient(135deg, #1565C0, #42A5F5)",
  color: "#fff",
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 700,
  fontSize: "0.8rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginTop: "2px",
});

const SubBadge = styled(Box)({
  display: "flex",
  gap: "12px",
  padding: "12px 16px",
  background: "#FAFAFA",
  border: "1px solid #E3F2FD",
  borderLeft: "3px solid #1565C0",
  borderRadius: "0 4px 4px 0",
  marginBottom: "10px",
});

const LogoBar = styled(Box)({
  background: "#F5F5F5",
  borderTop: "1px solid #E0E0E0",
  borderBottom: "1px solid #E0E0E0",
  padding: "16px 0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "wrap",
  gap: "0",
});

const LogoDivider = styled(Box)({
  width: "1px",
  height: "36px",
  background: "#BDBDBD",
  margin: "0 20px",
});

const LogoItem = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "4px",
});

// ── Data ──────────────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: <MapIcon sx={{ fontSize: 20 }} />, title: "Hazard & Risk Map Visualization", desc: "Interactive spatial layers for national and local hazard and risk assessment." },
  { icon: <LayersIcon sx={{ fontSize: 20 }} />, title: "Exposure Data Integration", desc: "Critical infrastructure and community exposure for rapid impact assessment." },
  { icon: <RadarIcon sx={{ fontSize: 20 }} />, title: "Rainfall-Based Hazard Monitoring", desc: "Real-time rainfall analysis supporting landslide risk and anticipatory triggers." },
  { icon: <SatelliteAltIcon sx={{ fontSize: 20 }} />, title: "GIS & Remote Sensing Mapping", desc: "Satellite-derived mapping of buildings, facilities, and land use." },
  { icon: <DashboardIcon sx={{ fontSize: 20 }} />, title: "Stakeholder Dashboards", desc: "User-friendly dashboards tailored for national and sectoral decision-makers." },
  { icon: <LinkIcon sx={{ fontSize: 20 }} />, title: "Impact-Based Forecasting Links", desc: "Integration with climate and forecast systems for actionable risk guidance." },
];

const STAKEHOLDERS = [
  { title: "Civil Protection Authority (APC)", desc: "National lead for disaster preparedness, anticipatory action, and emergency response coordination." },
  { title: "National Meteorology & Geophysics Directorate (DNMG)", desc: "Provides authoritative weather, climate, and geophysical data into the platform." },
  { title: "Disaster Management Institutions", desc: "Sectoral agencies accessing hazard maps, exposure layers, and operational decision-support tools." },
  { title: "National & Local Stakeholders", desc: "Communities, planners, and first responders using dashboards for preparedness and response planning." },
];

const SUBCOMPONENTS = [
  { num: "1", text: "Establishing the DRR Decision Support System platform for Timor-Leste with integrated hazard data layers." },
  { num: "2", text: "Integration of hazard maps, remote sensing data, and GIS-based exposure layers for risk visualization." },
  { num: "3", text: "Building capacity of stakeholders to apply hazard and climate information for operational decision-making." },
  { num: "4", text: "Supporting impact-based forecasting linkages with DNMG and national meteorological systems." },
];

// ── Component ─────────────────────────────────────────────────────────────────
const About = () => {
  return (
    <PageWrapper>

      {/* HERO BANNER */}
      <HeroBanner>
        <Container maxWidth={false} sx={{ maxWidth: "100% !important", px: 3 }}>
          <HeroTitle>About DRR Decision Support System</HeroTitle>
        </Container>
      </HeroBanner>

      {/* MAIN CONTENT */}
      <Container maxWidth={false} sx={{ maxWidth: "100% !important", px: 3, py: 4.5 }}>
        <Grid container spacing={3}>

          {/* LEFT — Main editorial content */}
          <Grid item xs={12} md={8}>

            {/* Overview */}
            <ContentCard>
              <SectionDivider />
              <SectionHeading>Overview</SectionHeading>
              <BodyParagraph>
                The Disaster Risk Reduction Decision Support System (DRR DSS) is a digital platform designed
                to strengthen disaster preparedness, early warning, and risk-informed decision-making in
                Timor-Leste. The system supports national and sectoral agencies in translating weather, climate,
                hazard, exposure, and vulnerability information into practical guidance for anticipatory action
                and disaster risk management.
              </BodyParagraph>
              <BodyParagraph>
                Timor-Leste is highly exposed to hydro-meteorological hazards including floods, landslides,
                strong winds, tropical cyclones, droughts, and other climate-related risks. Floods and
                landslides remain among the most serious hazards affecting lives, livelihoods, agriculture,
                infrastructure, water supply, schools, and health facilities across the country.
              </BodyParagraph>
              <BodyParagraph>
                The DRR DSS has been developed under the Green Climate Fund-supported project{" "}
                <Box component="span" sx={{ fontStyle: "italic", color: "#1565C0", fontWeight: 500 }}>
                  "Enhancing Early Warning Systems to Build Greater Resilience to Hydro-Meteorological
                  Hazards in Timor-Leste,"
                </Box>{" "}
                implemented with the support of UNEP, APC, DNMG, RIMES, and national stakeholders.
                It contributes to establishing impact-based forecasting and decision-support systems for
                disaster risk reduction, agriculture, marine, and other climate-sensitive sectors.
              </BodyParagraph>
            </ContentCard>

            {/* Purpose & Target Users */}
            <ContentCard>
              <SectionDivider />
              <SectionHeading>Purpose & Target Users</SectionHeading>
              <BodyParagraph>
                The system provides a common platform for accessing hazard information, visualizing risk
                areas, identifying exposed assets and vulnerable communities, and supporting preparedness
                and response planning through integration of exposure data, hazard maps, remote sensing
                and GIS-based information.
              </BodyParagraph>
              <Box>
                {STAKEHOLDERS.map((s, i) => (
                  <StakeholderRow key={i}>
                    <AccentNumber>{i + 1}</AccentNumber>
                    <Box>
                      <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: "0.95rem", color: "#1565C0", mb: 0.3, lineHeight: 1.4 }}>
                        {s.title}
                      </Typography>
                      <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontSize: "0.9rem", color: "#616161", lineHeight: 1.7 }}>
                        {s.desc}
                      </Typography>
                    </Box>
                  </StakeholderRow>
                ))}
              </Box>
            </ContentCard>

            {/* Hazards */}
            {/* <ContentCard>
              <SectionDivider />
              <SectionHeading>Hydro-Meteorological Hazards Addressed</SectionHeading>
              <BodyParagraph>
                Through integration of available exposure data, hazard maps, remote sensing and GIS-based
                information, the platform helps users understand where hazards may occur, who or what may
                be affected, and what actions are needed before, during, and after hazardous events.
              </BodyParagraph>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {HAZARDS.map((h) => (
                  <Grid item xs={6} sm={4} key={h.label}>
                    <HazardTag>
                      <Box sx={{ color: "#1565C0" }}>{h.icon}</Box>
                      <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontSize: "0.88rem", fontWeight: 600, color: "#424242" }}>
                        {h.label}
                      </Typography>
                    </HazardTag>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, alignItems: "center" }}>
                <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontSize: "0.82rem", color: "#757575", fontStyle: "italic", mr: 0.5 }}>
                  Affected sectors:
                </Typography>
                {["Lives & Livelihoods", "Agriculture", "Infrastructure", "Water Supply", "Schools", "Health Facilities"].map((tag) => (
                  <Chip key={tag} label={tag} size="small" sx={{
                    fontFamily: "'Roboto', sans-serif", fontSize: "0.75rem",
                    background: "#E3F2FD", color: "#1565C0",
                    border: "1px solid #90CAF9", borderRadius: "3px", height: "24px",
                  }} />
                ))}
              </Box>
            </ContentCard> */}

            {/* Sub-components */}
            <ContentCard>
              <SectionDivider />
              <SectionHeading>Platform Components</SectionHeading>
              <BodyParagraph>
                The DRR DSS is composed of integrated components delivering end-to-end disaster risk
                intelligence — from data ingestion through to stakeholder decision support and capacity
                development.
              </BodyParagraph>
              {SUBCOMPONENTS.map((s) => (
                <SubBadge key={s.num}>
                  <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: "0.9rem", color: "#1565C0", minWidth: "18px", flexShrink: 0, mt: "1px" }}>
                    {s.num}.
                  </Typography>
                  <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontSize: "0.92rem", color: "#424242", lineHeight: 1.75 }}>
                    {s.text}
                  </Typography>
                </SubBadge>
              ))}
            </ContentCard>

          </Grid>

          {/* RIGHT — Sidebar */}
          <Grid item xs={12} md={4}>

            {/* Key Features */}
            <ContentCard sx={{ mb: 2.5 }}>
              <SectionDivider />
              <SectionHeading sx={{ fontSize: "1.15rem" }}>Key Features</SectionHeading>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                {FEATURES.map((f) => (
                  <FeatureItem key={f.title}>
                    <FeatureIconWrap>{f.icon}</FeatureIconWrap>
                    <Box>
                      <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: "0.87rem", color: "#1565C0", mb: 0.3, lineHeight: 1.4 }}>
                        {f.title}
                      </Typography>
                      <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontSize: "0.8rem", color: "#616161", lineHeight: 1.65 }}>
                        {f.desc}
                      </Typography>
                    </Box>
                  </FeatureItem>
                ))}
              </Box>
            </ContentCard>

            {/* Implementing Partners */}
            {/* <ContentCard sx={{ mb: 2.5 }}>
              <SectionDivider />
              <SectionHeading sx={{ fontSize: "1.15rem" }}>Implementing Partners</SectionHeading>
              <Box>
                {[
                  { abbr: "APC", name: "Civil Protection Authority", role: "Lead National Agency" },
                  { abbr: "DNMG", name: "Natl. Meteorology & Geophysics Directorate", role: "Meteorological Authority" },
                  { abbr: "UNEP", name: "UN Environment Programme", role: "Implementation Partner" },
                  { abbr: "RIMES", name: "Regional Integrated Multi-Hazard EWS", role: "Technical Support" },
                  { abbr: "GCF", name: "Green Climate Fund", role: "Funding Partner" },
                ].map((p, i, arr) => (
                  <Box key={p.abbr} sx={{ display: "flex", gap: "12px", py: 1.4, borderBottom: i < arr.length - 1 ? "1px solid #F5F5F5" : "none" }}>
                    <Box sx={{
                      width: 34, height: 34, flexShrink: 0, borderRadius: "4px",
                      background: "#E3F2FD", border: "1px solid #90CAF9",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: "0.63rem", color: "#1565C0", letterSpacing: "0.03em" }}>
                        {p.abbr}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: "0.83rem", color: "#424242", lineHeight: 1.4 }}>
                        {p.name}
                      </Typography>
                      <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontSize: "0.73rem", color: "#1565C0", mt: 0.3 }}>
                        {p.role}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </ContentCard> */}

            {/* GCF funding callout */}
            <Box sx={{
              padding: "16px 18px",
              background: "linear-gradient(135deg, #E3F2FD, #BBDEFB)",
              border: "1px solid #90CAF9",
              borderLeft: "4px solid #1565C0",
              borderRadius: "0 4px 4px 0",
              mb: 2.5,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <VerifiedIcon sx={{ fontSize: 16, color: "#1565C0" }} />
                <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: "0.83rem", color: "#1565C0" }}>
                  GCF-Funded Initiative
                </Typography>
              </Box>
              <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontSize: "0.8rem", color: "#424242", lineHeight: 1.75 }}>
                Supported by UN Environment Programme, RIMES, and the Green Climate Fund to enhance
                early warning systems and hydro-meteorological resilience across Timor-Leste.
              </Typography>
            </Box>

            {/* Partner logos */}
            {/* <ContentCard>
              <SectionDivider />
              <SectionHeading sx={{ fontSize: "1.15rem" }}>Partner Logos</SectionHeading>
              <Grid container spacing={1.2}>
                {[
                  { src: "/timor.png", alt: "Timor-Leste", h: 40 },
                  { src: "/dnmg1.png", alt: "DNMG", h: 36 },
                  { src: "/DNMG_NEW.png", alt: "DNMG New", h: 36 },
                  { src: "/LoginPage/UNEP.svg", alt: "UNEP", h: 34 },
                  { src: "/LoginPage/RIMES.svg", alt: "RIMES", h: 34 },
                  { src: "/LoginPage/GCF.svg", alt: "GCF", h: 40 },
                ].map((logo) => (
                  <Grid item xs={4} key={logo.alt}>
                    <PartnerLogoCard>
                      <img src={logo.src} alt={logo.alt} style={{ height: logo.h, objectFit: "contain", maxWidth: "100%" }} />
                    </PartnerLogoCard>
                  </Grid>
                ))}
              </Grid>
            </ContentCard> */}

          </Grid>
        </Grid>
      </Container>

      {/* LOGO BAR */}
      <LogoBar>
        <LogoItem>
          <img src="/timor.png" alt="Timor-Leste" style={{ height: 38, objectFit: "contain" }} />
          <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontSize: "0.63rem", color: "#757575", letterSpacing: "0.07em", textTransform: "uppercase" }}>Timor-Leste</Typography>
        </LogoItem>
        <LogoDivider />
        <LogoItem>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <img src="/dnmg1.png" alt="DNMG" style={{ height: 36, objectFit: "contain" }} />
            <img src="/DNMG_NEW.png" alt="DNMG New" style={{ height: 36, objectFit: "contain" }} />
          </Box>
          <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontSize: "0.63rem", color: "#757575", letterSpacing: "0.07em", textTransform: "uppercase" }}>DNMG</Typography>
        </LogoItem>
        <LogoDivider />
        <LogoItem>
          <img src="/LoginPage/UNEP.svg" alt="UNEP" style={{ height: 34, objectFit: "contain" }} />
          <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontSize: "0.63rem", color: "#757575", letterSpacing: "0.07em", textTransform: "uppercase" }}>UNEP</Typography>
        </LogoItem>
        <LogoDivider />
        <LogoItem>
          <img src="/LoginPage/RIMES.svg" alt="RIMES" style={{ height: 34, objectFit: "contain" }} />
          <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontSize: "0.63rem", color: "#757575", letterSpacing: "0.07em", textTransform: "uppercase" }}>RIMES</Typography>
        </LogoItem>
        <LogoDivider />
        <LogoItem>
          <img src="/LoginPage/GCF.svg" alt="GCF" style={{ height: 38, objectFit: "contain" }} />
          <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontSize: "0.63rem", color: "#757575", letterSpacing: "0.07em", textTransform: "uppercase" }}>Green Climate Fund</Typography>
        </LogoItem>
      </LogoBar>

      {/* FOOTER */}
      <Box sx={{ borderTop: "1px solid #E0E0E0", background: "#F5F5F5", py: 2.5, textAlign: "center" }}>
        <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontSize: "0.81rem", color: "#757575" }}>
          © {new Date().getFullYear()} DRR Decision Support System · Timor-Leste &nbsp;·&nbsp;
          Disaster Risk Reduction &amp; Early Warning for Hydro-Meteorological Hazards
        </Typography>
      </Box>

    </PageWrapper>
  );
};

export default About;